import ReactECharts from 'echarts-for-react';

/**
 * ProcessGraphChart — a directed process graph with an approval gate (blog06).
 * Steps are executed by bots; an edge that would cross to the outside world
 * carries a gate and pauses in review mode for a human.
 *
 * Chart-type-first: this is a FLOW / process graph → ECharts `graph` with
 * categories and directed edges (NOT a bar chart). Brand colors; opaque white bg.
 */

const BRAND = {
  teal: '#0AB3BF',
  tealDark: '#087B8B',
  info: '#3B82F6',
  success: '#10B981',
  warning: '#F59E0B',
  error: '#EF4444',
  slate: '#334155',
  muted: '#64748B',
  surface: '#FFFFFF',
};

const CATEGORIES = [
  { name: 'Step', itemStyle: { color: BRAND.teal } },
  { name: 'Gate', itemStyle: { color: BRAND.warning } },
  { name: 'External', itemStyle: { color: BRAND.error } },
  { name: 'Log', itemStyle: { color: BRAND.info } },
];

const NODES = [
  { name: 'Qualify lead', category: 0, x: 40, y: 40, symbolSize: 64 },
  { name: 'Draft outreach', category: 0, x: 40, y: 150, symbolSize: 64 },
  { name: 'Human review gate', category: 1, x: 40, y: 260, symbolSize: 76 },
  { name: 'Send email', category: 2, x: 40, y: 370, symbolSize: 68 },
  { name: 'Log the run', category: 3, x: 40, y: 480, symbolSize: 64 },
];

const EDGES = [
  { source: 'Qualify lead', target: 'Draft outreach' },
  { source: 'Draft outreach', target: 'Human review gate' },
  { source: 'Human review gate', target: 'Send email', lineStyle: { color: BRAND.error } },
  { source: 'Send email', target: 'Log the run', lineStyle: { color: BRAND.info } },
];

export const ProcessGraphChart = ({ height = 440 }: { height?: number }) => {
  const option = {
    backgroundColor: BRAND.surface,
    tooltip: {
      backgroundColor: '#1E293B',
      borderColor: BRAND.slate,
      textStyle: { color: '#F8FAFC', fontSize: 13 },
    },
    legend: {
      top: 8,
      textStyle: { color: BRAND.slate, fontSize: 13 },
      data: ['Step (bot)', 'Approval gate', 'External action', 'Log row'],
    },
    series: [
      {
        type: 'graph',
        layout: 'none',
        roam: true,
        draggable: true,
        data: NODES,
        links: EDGES,
        categories: CATEGORIES,
        symbol: 'circle',
        label: {
          show: true,
          position: 'bottom',
          color: BRAND.slate,
          fontSize: 12,
          fontWeight: 700,
          formatter: (p: { name: string }) => p.name,
        },
        lineStyle: { color: BRAND.slate, width: 2, curveness: 0.08 },
        edgeSymbol: ['none', 'arrow'],
        edgeSymbolSize: 10,
        emphasis: { focus: 'adjacency', lineStyle: { width: 4 } },
      },
    ],
  };

  return (
    <div className="my-8 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-white p-2">
      <ReactECharts option={option} style={{ height }} opts={{ renderer: 'canvas' }} />
      <p className="mt-2 text-xs text-slate-500 dark:text-slate-400 text-center">
        Figure 1. A process is a directed graph: bot steps feed a human review gate, and only an approved action crosses
        to the outside world — then the whole run is logged. Drag to reposition; the edge to the gate is the decision.
      </p>
    </div>
  );
};
